/**
 * PostHog Analytics Utility
 *
 * This file provides helper functions for tracking events with PostHog.
 * PostHog is initialized in index.tsx using the JavaScript SDK.
 *
 * Project: sudalash.com
 * API Host: https://eu.i.posthog.com
 */
import posthog from 'posthog-js'
// Check if PostHog is available and initialized
export const isPostHogAvailable = (): boolean => {
  return typeof window !== 'undefined' && posthog && posthog.__loaded
}
// Track a custom event
export const trackEvent = (
  eventName: string,
  properties?: Record<string, any>,
) => {
  if (!isPostHogAvailable()) {
    console.warn('PostHog not initialized yet')
    return
  }
  try {
    posthog.capture(eventName, properties)
  } catch (error) {
    console.error('Error tracking event:', error)
  }
}
// Track page view (useful for SPAs with client-side routing)
export const trackPageView = (pagePath?: string) => {
  if (!isPostHogAvailable()) return
  try {
    posthog.capture('$pageview', {
      $current_url: pagePath || window.location.href,
    })
  } catch (error) {
    console.error('Error tracking page view:', error)
  }
}
// Identify user (call this when you have user information)
export const identifyUser = (
  userId: string,
  properties?: Record<string, any>,
) => {
  if (!isPostHogAvailable()) return
  try {
    posthog.identify(userId, properties)
  } catch (error) {
    console.error('Error identifying user:', error)
  }
}
// Reset user (call this on logout or when user opts out)
export const resetUser = () => {
  if (!isPostHogAvailable()) return
  try {
    posthog.reset()
  } catch (error) {
    console.error('Error resetting user:', error)
  }
}
// Opt user out of tracking (for PDPA/GDPR compliance)
export const optOut = () => {
  if (!isPostHogAvailable()) return
  try {
    posthog.opt_out_capturing()
    console.log('User opted out of tracking')
  } catch (error) {
    console.error('Error opting out:', error)
  }
}
// Opt user in to tracking
export const optIn = () => {
  if (!isPostHogAvailable()) return
  try {
    posthog.opt_in_capturing()
    console.log('User opted in to tracking')
  } catch (error) {
    console.error('Error opting in:', error)
  }
}
// Check if user has opted out
export const hasOptedOut = (): boolean => {
  if (!isPostHogAvailable()) return false
  try {
    return posthog.has_opted_out_capturing()
  } catch (error) {
    console.error('Error checking opt-out status:', error)
    return false
  }
}
// Set user properties (for enriching user profiles)
export const setUserProperties = (properties: Record<string, any>) => {
  if (!isPostHogAvailable()) return
  try {
    posthog.setPersonProperties(properties)
  } catch (error) {
    console.error('Error setting user properties:', error)
  }
}
/**
 * Verification Helper
 * Run this in your browser console to test PostHog:
 *
 * import { verifyPostHog } from './utils/posthog'
 * verifyPostHog()
 */
export const verifyPostHog = () => {
  if (!isPostHogAvailable()) {
    console.error('❌ PostHog is not initialized')
    return
  }
  console.log('✅ PostHog is initialized')
  console.log('📊 Sending test event...')
  posthog.capture('verification_test', {
    timestamp: new Date().toISOString(),
    source: 'manual_verification',
    domain: window.location.hostname,
  })
  console.log(
    '✅ Test event sent! Check PostHog dashboard: Install → Verify Installation',
  )
  console.log('Expected domain: sudalash.com')
  console.log('API Host: https://eu.i.posthog.com')
}
// Event names as constants for consistency
export const EVENTS = {
  // Contact events
  EMAIL_CLICKED: 'email_clicked',
  WHATSAPP_CLICKED: 'whatsapp_clicked',
  LINKEDIN_CLICKED: 'linkedin_clicked',
  CALENDAR_BOOKING_STARTED: 'calendar_booking_started',
  CALENDAR_BOOKING_OPENED: 'calendar_booking_opened',
  // Download events
  CV_DOWNLOAD_CLICKED: 'cv_download_clicked',
  // Navigation events
  SECTION_VIEWED: 'section_viewed',
  LANGUAGE_CHANGED: 'language_changed',
  // Certification events
  CERTIFICATION_EXPANDED: 'certification_expanded',
  CERTIFICATION_VERIFIED: 'certification_verified',
  CERTIFICATION_FILTERED: 'certification_filtered',
  // Modal events
  CONNECT_MODAL_OPENED: 'connect_modal_opened',
  CONNECT_MODAL_CLOSED: 'connect_modal_closed',
  WORLDS_OVERLAY_OPENED: 'worlds_overlay_opened',
  WORLDS_OVERLAY_CLOSED: 'worlds_overlay_closed',
  // Engagement events
  CONFETTI_TRIGGERED: 'confetti_triggered',
  COMPETENCY_CARD_EXPANDED: 'competency_card_expanded',
  // Spotlight tour events
  SPOTLIGHT_STEP_VIEWED: 'spotlight_step_viewed',
  SPOTLIGHT_ACTION_CLICKED: 'spotlight_action_clicked',
  SPOTLIGHT_DISMISSED: 'spotlight_dismissed',
  SPOTLIGHT_COMPLETED: 'spotlight_completed',
  SPOTLIGHT_WIDGET_OPENED: 'spotlight_widget_opened',
  SPOTLIGHT_WIDGET_ACTION: 'spotlight_widget_action',
  SPOTLIGHT_TOUR_RESTARTED: 'spotlight_tour_restarted',
  SPOTLIGHT_TOUR_TOGGLED: 'spotlight_tour_toggled',
}
// Export posthog instance for advanced usage
export { posthog }
